import { ReactComponent as CloseIcon } from "../../../img/iconImgAllClose.svg";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "../css/TermAddScreen.module.css";
import Group from "../../groups/Group";
import TermAddField from "../custom_view/TermAddField";
import Term, { TermLevelUpdate, isValidTerm } from "../Term";
import { TermLevel } from "../custom_view/TermLevel";
import GroupSelectScreen from "../../groups/GroupSelectScreen";
import OnevocaDialog from "../../common/OnevocaDialog";
import {
  fetchAutoPronunciation,
  fetchTermFromDictionary,
  insertTerm,
} from "../termAPI";
import LoadingScreen from "../../common/LoadingScreen";
import OnevocaServerError, {
  errorNeedMoreSlot,
} from "../../common/OnevocaServerError";
import { useNavigate } from "react-router-dom";
import DropDownButton from "../../common/view/DropDownButton";
import ContainerNavigationView from "../../common/view/ContainerNavigationView";
import { setLatestSaveGroup } from "../../groups/utils/groupSettings";
import { fetchGroups } from "../../groups/groupAPI";
import { useBackToClose } from "../../../hooks/useBackToClose";
import { formatShortcut } from "../../common/util/shourtcut";
import i18n from "../../../i18n";
import Chip from "../../../components/ui/Chip";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import { CheckRounded, TuneRounded } from "@mui/icons-material";
import TermAddSettingSheet from "./TermAddSettingSheet";
import {
  TermAddLineBreakMode,
  getTermAddLineBreakModeFromLocalStorage,
  isOnTermAddDuplicatedCheckFromLocalStorage,
  isShowingTermAddMemoFromLocalStorage,
  isShowingTermAddPronunciationFromLocalStorage,
  setTermAddDuplicatedCheckToLocalStorage,
  setTermAddLineBreakModeToLocalStorage,
  setTermAddShowingMemoToLocalStorage,
  setTermAddShowingPronunciationToLocalStorage,
} from "../../settings/util/termAddSettings";
import {
  fetchUserSettings,
  patchAutoPronunciation,
} from "../../settings/data/UserSettingsRemoteDataSource";
import { fetchIsSubscriber } from "../../subscribe/service/subscribeAPI";

interface TermAddScreenProps {
  open: boolean;
  selectedGroup?: Group;
  onSaved: () => void;
  onClose: () => void;
}

// 모바일 앱과 같이 입력이 멈춘 뒤에 사전 뜻과 발음을 요청한다
const autoFillDebounceMilliseconds = 250;

function makeEmptyTerm(): Term {
  return new Term(
    "0",
    "",
    "",
    "",
    "",
    "",
    TermLevel.difficult,
    0,
    0,
    false,
    false,
    TermLevelUpdate.none
  );
}

function getGroupLanguageCode(group?: Group): string | undefined {
  if (group === undefined || group.isAllGroup || group.isUnspecifiedGroup) {
    return undefined;
  }
  return group.language?.code;
}

export default function TermAddScreen(props: TermAddScreenProps) {
  useBackToClose(props.open, props.onClose);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group>();
  const [term, setTerm] = useState<Term>(makeEmptyTerm());
  const [dictionaryDefinitions, setDictionaryDefinitions] = useState<string[]>(
    []
  );
  const [pronunciationHint, setPronunciationHint] = useState("");
  const [showGroupSelectScreen, setShowGroupSelectScreen] = useState(false);
  const [showSlotNeedMessage, setShowSlotNeedMessage] = useState(false);
  const [showSettingSheet, setShowSettingSheet] = useState(false);
  const [duplicatedTerms, setDuplicatedTerms] = useState<Term[] | null>(null);

  const [lineBreakMode, setLineBreakMode] = useState(
    getTermAddLineBreakModeFromLocalStorage()
  );
  const [isOnDuplicatedCheck, setIsOnDuplicatedCheck] = useState(
    isOnTermAddDuplicatedCheckFromLocalStorage()
  );
  const [showMemo, setShowMemo] = useState(
    isShowingTermAddMemoFromLocalStorage()
  );
  const [showPronunciation, setShowPronunciation] = useState(
    isShowingTermAddPronunciationFromLocalStorage()
  );
  // 발음 자동 완성은 서버에 저장되는 구독자 전용 설정이다
  const [isSubscriber, setIsSubscriber] = useState(false);
  const [isOnAutoPronunciation, setIsOnAutoPronunciation] = useState(false);
  const isAutoPronunciationAvailable =
    isSubscriber && isOnAutoPronunciation && showPronunciation;

  const firstInputRef = useRef<HTMLTextAreaElement | null>(null);
  const autoFillTimeout = useRef<NodeJS.Timeout | null>(null);
  // 늦게 도착한 이전 단어의 응답이 반영되지 않도록 요청마다 번호를 매긴다
  const autoFillRequestId = useRef(0);

  const saveShortcut = formatShortcut({ mac: "⌘ S", win: "Ctrl + S" });

  useEffect(() => {
    if (props.open === true) {
      setSelectedGroup(props.selectedGroup);
      cleanSelectedGroupIfNeeded(props.selectedGroup);
      setTimeout(() => firstInputRef.current?.focus(), 0);
      fetchAutoPronunciationSetting();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  useEffect(() => {
    requestAutoFill(term.term);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup, isAutoPronunciationAvailable]);

  const saveRef = useRef<() => void>(() => {});
  saveRef.current = save;

  useEffect(() => {
    if (props.open !== true) return;

    const onKeyDown = (e: KeyboardEvent) => {
      const isSave = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s";

      if (!isSave) return;

      e.preventDefault();
      saveRef.current();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [props.open]);

  useEffect(() => {
    return () => {
      autoFillTimeout.current && clearTimeout(autoFillTimeout.current);
    };
  }, []);

  // 캐싱된 그룹이 삭제된 그룹이라면 '그룹 미선택'으로 초기화하고,
  // 존재하는 그룹이라면 언어 등 최신 정보로 교체한다
  async function cleanSelectedGroupIfNeeded(group?: Group) {
    if (group === undefined || group.isAllGroup || group.isUnspecifiedGroup) {
      return;
    }

    let groups: Group[];
    try {
      groups = await fetchGroups();
    } catch {
      // 확인 실패 시 기존 선택을 유지한다
      return;
    }

    const verifiedGroup = groups.find((g) => g.title === group.title);

    // 확인하는 사이 사용자가 다른 그룹을 선택했다면 덮어쓰지 않는다
    setSelectedGroup((current) => {
      if (current?.title !== group.title) {
        return current;
      }
      if (verifiedGroup === undefined) {
        return undefined;
      }
      // 언어가 같다면 불필요한 발음 재요청을 막기 위해 기존 객체를 유지한다
      return getGroupLanguageCode(current) === getGroupLanguageCode(verifiedGroup)
        ? current
        : verifiedGroup;
    });
  }

  async function fetchAutoPronunciationSetting() {
    const [subscriber, userSettings] = await Promise.all([
      fetchIsSubscriber().catch(() => false),
      fetchUserSettings().catch(() => undefined),
    ]);
    setIsSubscriber(subscriber);
    setIsOnAutoPronunciation(userSettings?.isAutoPronunciation ?? false);
  }

  async function toggleAutoPronunciation() {
    if (!isSubscriber) {
      alert(t("MoreExamplesSubscriberOnlyMessage"));
      return;
    }

    const isOn = !isOnAutoPronunciation;
    setIsOnAutoPronunciation(isOn);
    try {
      await patchAutoPronunciation(isOn);
    } catch (error) {
      setIsOnAutoPronunciation(!isOn);
      alert(error instanceof Error ? error.message : error);
    }
  }

  function toggleShowingMemo() {
    setTermAddShowingMemoToLocalStorage(!showMemo);
    setShowMemo(!showMemo);
  }

  function toggleShowingPronunciation() {
    setTermAddShowingPronunciationToLocalStorage(!showPronunciation);
    setShowPronunciation(!showPronunciation);
  }

  function updateLineBreakMode(mode: TermAddLineBreakMode) {
    setTermAddLineBreakModeToLocalStorage(mode);
    setLineBreakMode(mode);
  }

  function updateDuplicatedCheck(isOn: boolean) {
    setTermAddDuplicatedCheckToLocalStorage(isOn);
    setIsOnDuplicatedCheck(isOn);
  }

  function requestAutoFill(word: string) {
    autoFillTimeout.current && clearTimeout(autoFillTimeout.current);
    const requestId = ++autoFillRequestId.current;

    if (word.length === 0) {
      clearAutoFill();
      return;
    }

    autoFillTimeout.current = setTimeout(() => {
      const isLatestRequest = () => requestId === autoFillRequestId.current;

      if (i18n.language.startsWith("ko")) {
        fetchTermFromDictionary(word)
          .catch(() => [])
          .then((definitions) => {
            if (!isLatestRequest()) return;
            setDictionaryDefinitions(definitions);
            setTerm((prev) => ({ ...prev, defaultHints: definitions.join(", ") }));
          });
      }

      const language = getGroupLanguageCode(selectedGroup);
      if (isAutoPronunciationAvailable && language !== undefined) {
        fetchAutoPronunciation(word, language)
          .catch(() => "")
          .then((pronunciation) => {
            if (!isLatestRequest()) return;
            setPronunciationHint(pronunciation);
          });
      } else {
        setPronunciationHint("");
      }
    }, autoFillDebounceMilliseconds);
  }

  function clearAutoFill() {
    setDictionaryDefinitions([]);
    setPronunciationHint("");
    setTerm((prev) => ({ ...prev, defaultHints: undefined }));
  }

  function updateTermWord(value: string) {
    setTerm((prev) => ({ ...prev, term: value }));
    requestAutoFill(value);
  }

  function updateTermDefinition(value: string) {
    setTerm((prev) => ({ ...prev, definition: value }));
  }

  function updateTermMemo(value: string) {
    setTerm((prev) => ({ ...prev, memo: value }));
  }

  function updateTermPronunciation(value: string) {
    setTerm((prev) => ({ ...prev, pronunciation: value }));
  }

  function selectDictionaryDefinition(definition: string) {
    setTerm((prev) => ({
      ...prev,
      definition:
        prev.definition.length === 0
          ? definition
          : `${prev.definition}, ${definition}`,
      defaultHints: undefined,
    }));
  }

  function moveToSlotPurchase() {
    navigate("/slot-purchase");
  }

  function handleOnClickBackground(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
  }

  function handleOnClickCancelButton() {
    closeScreen();
  }

  // 저장 버튼 · 단축키 · 엔터키로 저장할 때. 다른 창이 떠 있으면 무시한다
  function save() {
    if (
      isLoading ||
      showGroupSelectScreen ||
      showSlotNeedMessage ||
      showSettingSheet ||
      duplicatedTerms !== null
    ) {
      return;
    }
    saveTerm(isOnDuplicatedCheck);
  }

  async function saveTerm(isCheckDuplicated: boolean) {

    if (selectedGroup === undefined) {
      alert(t("SelectGroupMessage"));
      return;
    }

    const termForSave: Term = {
      ...term,
      term: term.term.trim(),
      definition: term.definition.trim(),
      memo: term.memo.trim(),
      pronunciation:
        term.pronunciation.trim().length !== 0
          ? term.pronunciation.trim()
          : pronunciationHint.trim(),
    };

    if (!isValidTerm(termForSave)) {
      alert(t("MoreThanOneTermMessage"));
      return;
    }

    const selectedGroupname =
      selectedGroup.isAllGroup || selectedGroup.isUnspecifiedGroup
        ? ""
        : selectedGroup.title;
    setIsLoading(true);
    try {
      const duplicated = await insertTerm(
        termForSave,
        selectedGroupname,
        isCheckDuplicated
      );
      if (duplicated.length !== 0) {
        setDuplicatedTerms(duplicated);
        return;
      }
      setLatestSaveGroup(selectedGroup);
      finishSaveTerm();
    } catch (error) {
      if (error instanceof OnevocaServerError) {
        if (error.name === errorNeedMoreSlot) {
          setShowSlotNeedMessage(true);
        } else {
          alert(error.message);
        }
      } else if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
    } finally {
      setIsLoading(false);
    }
  }

  // 모바일 앱과 같이 창을 닫지 않고 입력칸을 비워 다음 단어를 바로 입력할 수 있게 한다
  function finishSaveTerm() {
    resetTerm();
    firstInputRef.current?.focus();
    props.onSaved();
  }

  function resetTerm() {
    autoFillTimeout.current && clearTimeout(autoFillTimeout.current);
    autoFillRequestId.current++;
    setTerm(makeEmptyTerm());
    setDictionaryDefinitions([]);
    setPronunciationHint("");
  }

  // 모바일 앱의 '등록된 단어 보기' 대신 이미 등록된 단어를 안내 문구 아래에 바로 보여준다
  function getDuplicatedTermsMessage(): string {
    if (duplicatedTerms === null) return "";
    const termsText = duplicatedTerms
      .map((duplicated) =>
        duplicated.definition.length !== 0
          ? `${duplicated.term} · ${duplicated.definition}`
          : duplicated.term
      )
      .join("\n");
    return `${t("DuplicatedTermMessage")}\n\n${termsText}`;
  }

  function closeScreen() {
    resetTerm();
    setSelectedGroup(undefined);
    props.onClose();
  }

  if (props.open === true) {
    return (
      <>
        <div className={styles.stack}>
          <div className={styles.container} onClick={handleOnClickBackground}>
            <div className={styles.termAddContainer}>
              <div className={styles.topContainer}>
                <ContainerNavigationView
                  open={true}
                  titleView={
                    <DropDownButton
                      title={
                        selectedGroup === undefined ||
                        selectedGroup.isUnspecifiedGroup
                          ? t("SelectGroupButton")
                          : selectedGroup.title
                      }
                      handleOnClick={() => {
                        setShowGroupSelectScreen(true);
                      }}
                    />
                  }
                  leftImageButtonImage={CloseIcon}
                  rightTextButtonText={t("Save")}
                  rightTextButtonColor="var(--text-accent-color)"
                  onClickLeftImageButton={handleOnClickCancelButton}
                  onClickRightTextButton={() => save()}
                />
              </div>
              <div className={styles.toolbar}>
                <Chip
                  selected={showMemo}
                  leadingIcon={showMemo ? <CheckRounded /> : undefined}
                  onClick={toggleShowingMemo}
                >
                  {t("Description")}
                </Chip>
                <Chip
                  selected={showPronunciation}
                  leadingIcon={showPronunciation ? <CheckRounded /> : undefined}
                  onClick={toggleShowingPronunciation}
                >
                  {t("Pronunciation")}
                </Chip>
                {showPronunciation && (
                  <Chip
                    selected={isSubscriber && isOnAutoPronunciation}
                    leadingIcon={
                      isSubscriber && isOnAutoPronunciation ? (
                        <CheckRounded />
                      ) : undefined
                    }
                    title={t("AutoFillPronunciationDescription")}
                    onClick={toggleAutoPronunciation}
                  >
                    {t("AutoFillPronunciation")}
                  </Chip>
                )}
                <Chip
                  leadingIcon={<TuneRounded />}
                  onClick={() => setShowSettingSheet(true)}
                >
                  {t("Settings")}
                </Chip>
              </div>
              <div className={styles.groupSelectContainer}></div>
              <div className={styles.termAddFieldsContainer}>
                <TermAddField
                  term={term}
                  dictionaryDefinitions={dictionaryDefinitions}
                  pronunciationHint={pronunciationHint}
                  showMemo={showMemo}
                  showPronunciation={showPronunciation}
                  lineBreakMode={lineBreakMode}
                  onSubmit={() => save()}
                  onChangeTerm={updateTermWord}
                  onChangeDefinition={updateTermDefinition}
                  onChangeMemo={updateTermMemo}
                  onChangePronunciation={updateTermPronunciation}
                  onSelectDictionaryDefinition={selectDictionaryDefinition}
                  firstInputRef={(el) => (firstInputRef.current = el)}
                />
                <p className={styles.termAddFieldTabKeyTipText}>
                  {t("TermAddSaveShortcutTipMessage", {
                    shortcut: saveShortcut,
                  })}
                </p>
              </div>
            </div>
          </div>
          <GroupSelectScreen
            open={showGroupSelectScreen === true}
            selectStyle="single"
            showAllGroup={false}
            showNoGroup={false}
            showGroupAddButton={true}
            selectedGroups={
              selectedGroup === undefined ? undefined : [selectedGroup.title]
            }
            handleOnSelectGroups={(groups: Group[]) => {
              if (groups.length !== 0) {
                const group = groups[0];
                setSelectedGroup(group);
              }
            }}
            handleOnClose={() => {
              setShowGroupSelectScreen(false);
            }}
          />
          <OnevocaDialog
            open={showSlotNeedMessage === true}
            title={t("Notice")}
            message={t("SlotNeedDialogMessage")}
            buttons={[
              {
                title: t("SlotAddButton"),
                style: "solid",
                onClick: () => {
                  setShowSlotNeedMessage(false);
                  moveToSlotPurchase();
                },
              },
              {
                title: t("Cancel"),
                style: "normal",
                onClick: () => {
                  setShowSlotNeedMessage(false);
                },
              },
            ]}
          />
          <TermAddSettingSheet
            open={showSettingSheet}
            onClose={() => setShowSettingSheet(false)}
            lineBreakMode={lineBreakMode}
            onChangeLineBreakMode={updateLineBreakMode}
            isOnDuplicatedCheck={isOnDuplicatedCheck}
            onChangeDuplicatedCheck={updateDuplicatedCheck}
          />
          <ConfirmDialog
            open={duplicatedTerms !== null}
            title={t("Notice")}
            message={getDuplicatedTermsMessage()}
            vertical
            onClose={() => setDuplicatedTerms(null)}
            actions={[
              {
                label: t("SaveAnywayButton"),
                variant: "danger",
                onClick: () => {
                  setDuplicatedTerms(null);
                  saveTerm(false);
                },
              },
              {
                label: t("Cancel"),
                variant: "ghost",
                onClick: () => setDuplicatedTerms(null),
              },
            ]}
          />
          {isLoading === true ? <LoadingScreen /> : null}
        </div>
      </>
    );
  } else {
    return null;
  }
}
